import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import Player from '@vimeo/player';
import { useGlobal } from '../../context/GlobalContext';
import { getStrapiMediaAttributes } from '../../lib/strapi';
import { getUiText } from '../../lib/uiText';
import playIcon from '../../assets/icons/icon_play_popup.svg';
import pauseIcon from '../../assets/icons/icon_pause_popup.svg';
import audioOnIcon from '../../assets/icons/icon_audio_on_popup.svg';
import audioOffIcon from '../../assets/icons/icon_audio_off_popup.svg';
import closeIcon from '../../assets/icons/icon_cerrar.svg';
import styles from './PopupFullscreen.module.css';

const VIDEO_EXTENSIONS_REGEX = /\.(mp4|webm|ogg|mov|m4v)(?:$|[?#])/i;
const SESSION_STORAGE_PREFIX = 'popup-fullscreen';
const CONTROL_HIDE_DELAY_MS = 2000;
const DEFAULT_VIMEO_META = {
  thumbnail: '',
  width: 16,
  height: 9,
};

const isVimeoUrl = (value) => {
  try {
    const url = new URL(value);
    return url.hostname.includes('vimeo.com');
  } catch {
    return false;
  }
};

const getVimeoId = (value) => {
  try {
    const url = new URL(value);
    const match = url.hostname.includes('player.vimeo.com')
      ? url.pathname.match(/\/video\/(\d+)/)
      : url.pathname.match(/\/(\d+)(?:$|\/)/);

    return match?.[1] || '';
  } catch {
    return '';
  }
};

const getVimeoCanonicalUrl = (value) => {
  const videoId = getVimeoId(value);
  return videoId ? `https://vimeo.com/${videoId}` : value;
};

const getVimeoEmbedUrl = (value, { autoplay = false, muted = false } = {}) => {
  const videoId = getVimeoId(value);
  if (!videoId) return value;

  const params = new URLSearchParams({
    autoplay: autoplay ? '1' : '0',
    muted: muted ? '1' : '0',
    controls: '0',
    title: '0',
    byline: '0',
    portrait: '0',
    badge: '0',
    dnt: '1',
    playsinline: '1',
    autopause: '0',
  });

  return `https://player.vimeo.com/video/${videoId}?${params.toString()}`;
};

const getPopupLabel = (popup, locale) => popup?.title?.trim()
  || popup?.pageTitle?.trim()
  || getUiText(locale, 'popupFullscreen');

const resolveMediaType = (popupMediaType, mediaSrc) => {
  if (popupMediaType === 'image' || popupMediaType === 'video') {
    return popupMediaType;
  }

  if (isVimeoUrl(mediaSrc) || VIDEO_EXTENSIONS_REGEX.test(mediaSrc || '')) {
    return 'video';
  }

  return 'image';
};

const buildSessionStorageKey = (popup, mediaSrc) => {
  const entryKey = popup?.id
    || popup?.documentId
    || popup?.pageDocumentId
    || mediaSrc
    || popup?.pageSlug
    || 'default';

  return `${SESSION_STORAGE_PREFIX}:${entryKey}`;
};

const PopupFullscreen = ({ popup }) => {
  const { locale } = useGlobal();
  const label = getPopupLabel(popup, locale);

  const resolvedMedia = useMemo(() => {
    if (!popup) {
      return {
        src: null,
        alt: 'Popup fullscreen',
        posterSrc: null,
        mediaType: 'image',
        isVimeo: false,
      };
    }

    const mediaAsset = popup.mediaUrl
      ? getStrapiMediaAttributes(popup.mediaUrl, {
        altFallback: label,
        titleFallback: label,
      })
      : getStrapiMediaAttributes(popup.media, {
        altFallback: label,
        titleFallback: label,
      });

    const posterAsset = popup.posterImage
      ? getStrapiMediaAttributes(popup.posterImage, {
        altFallback: getUiText(locale, 'poster', { label }),
        titleFallback: getUiText(locale, 'poster', { label }),
      })
      : { src: null };

    const mediaType = resolveMediaType(popup.mediaType, mediaAsset.src);

    return {
      src: mediaAsset.src,
      alt: mediaAsset.alt || label,
      posterSrc: posterAsset.src,
      mediaType,
      isVimeo: mediaType === 'video' && isVimeoUrl(mediaAsset.src),
    };
  }, [label, locale, popup]);

  const storageKey = useMemo(
    () => (popup && resolvedMedia.src ? buildSessionStorageKey(popup, resolvedMedia.src) : ''),
    [popup, resolvedMedia.src]
  );
  const isSessionMemoryEnabled = popup?.recordarPorSesion !== false;
  const popupInstanceKey = storageKey || popup?.id || popup?.documentId || popup?.pageDocumentId || popup?.pageSlug || resolvedMedia.src || '';

  const [dismissedPopupKey, setDismissedPopupKey] = useState('');
  const [hasStarted, setHasStarted] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCenterControlVisible, setIsCenterControlVisible] = useState(true);
  const [isMuted, setIsMuted] = useState(false);
  const [vimeoStartMuted, setVimeoStartMuted] = useState(false);
  const [vimeoMeta, setVimeoMeta] = useState(DEFAULT_VIMEO_META);
  const videoRef = useRef(null);
  const vimeoIframeRef = useRef(null);
  const vimeoPlayerRef = useRef(null);
  const hideControlsTimeoutRef = useRef(null);

  const vimeoCardStyle = useMemo(() => ({
    '--popup-vimeo-aspect-ratio': `${vimeoMeta.width} / ${vimeoMeta.height}`,
  }), [vimeoMeta.height, vimeoMeta.width]);

  const clearControlsHideTimeout = useCallback(() => {
    if (hideControlsTimeoutRef.current) {
      window.clearTimeout(hideControlsTimeoutRef.current);
      hideControlsTimeoutRef.current = null;
    }
  }, []);

  const showCenterControlTemporarily = useCallback(() => {
    setIsCenterControlVisible(true);

    if (!isPlaying) {
      clearControlsHideTimeout();
      return;
    }

    clearControlsHideTimeout();
    hideControlsTimeoutRef.current = window.setTimeout(() => {
      setIsCenterControlVisible(false);
      hideControlsTimeoutRef.current = null;
    }, CONTROL_HIDE_DELAY_MS);
  }, [clearControlsHideTimeout, isPlaying]);

  const syncPlaybackState = useCallback((nextIsPlaying) => {
    setIsPlaying(nextIsPlaying);

    if (!nextIsPlaying) {
      clearControlsHideTimeout();
      setIsCenterControlVisible(true);
      return;
    }

    setIsCenterControlVisible(true);
    clearControlsHideTimeout();
    hideControlsTimeoutRef.current = window.setTimeout(() => {
      setIsCenterControlVisible(false);
      hideControlsTimeoutRef.current = null;
    }, CONTROL_HIDE_DELAY_MS);
  }, [clearControlsHideTimeout]);

  const handleVideoSurfaceInteraction = useCallback(() => {
    if (resolvedMedia.mediaType !== 'video' || !isPlaying) return;
    showCenterControlTemporarily();
  }, [isPlaying, resolvedMedia.mediaType, showCenterControlTemporarily]);

  const resetPlayback = useCallback(() => {
    clearControlsHideTimeout();

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.currentTime = 0;
    }

    setVimeoStartMuted(false);
    setHasStarted(false);
    setIsCenterControlVisible(true);
    setIsPlaying(false);
    setIsMuted(false);
  }, [clearControlsHideTimeout]);

  const closePopup = useCallback(() => {
    if (isSessionMemoryEnabled && typeof window !== 'undefined' && storageKey) {
      window.sessionStorage.setItem(storageKey, 'closed');
    }

    resetPlayback();
    setDismissedPopupKey(popupInstanceKey);
  }, [isSessionMemoryEnabled, popupInstanceKey, resetPlayback, storageKey]);

  const handlePlaybackToggle = useCallback((event) => {
    event.stopPropagation();

    if (resolvedMedia.mediaType !== 'video') return;

    if (resolvedMedia.isVimeo) {
      if (!hasStarted) {
        setVimeoStartMuted(isMuted);
        setHasStarted(true);
        syncPlaybackState(true);
        return;
      }

      const player = vimeoPlayerRef.current;
      if (!player) return;

      const action = isPlaying ? player.pause() : player.play();
      action.catch(() => {
        setIsPlaying((current) => (isPlaying ? current : false));
      });
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    if (isPlaying) {
      video.pause();
      syncPlaybackState(false);
      return;
    }

    const playPromise = video.play();
    syncPlaybackState(true);

    if (typeof playPromise?.catch === 'function') {
      playPromise.catch(() => {
        syncPlaybackState(false);
      });
    }
  }, [hasStarted, isMuted, isPlaying, resolvedMedia.isVimeo, resolvedMedia.mediaType, syncPlaybackState]);

  const handleMuteToggle = useCallback((event) => {
    event.stopPropagation();

    const nextMuted = !isMuted;

    if (resolvedMedia.isVimeo && hasStarted) {
      const player = vimeoPlayerRef.current;
      if (!player) return;

      player.setMuted(nextMuted)
        .then(() => {
          setIsMuted(nextMuted);
        })
        .catch(() => {});
      return;
    }

    setIsMuted(nextMuted);
  }, [hasStarted, isMuted, resolvedMedia.isVimeo]);

  const isClosedInSession = Boolean(
    popup
    && resolvedMedia.src
    && typeof window !== 'undefined'
    && isSessionMemoryEnabled
    && storageKey
    && window.sessionStorage.getItem(storageKey) === 'closed'
  );
  const isOpen = Boolean(
    popup
    && resolvedMedia.src
    && dismissedPopupKey !== popupInstanceKey
    && !isClosedInSession
  );

  useEffect(() => {
    if (!resolvedMedia.isVimeo || !resolvedMedia.src || !isOpen || hasStarted) {
      return undefined;
    }

    let cancelled = false;

    const loadVimeoPreview = async () => {
      try {
        const response = await fetch(
          `https://vimeo.com/oembed.json?url=${encodeURIComponent(getVimeoCanonicalUrl(resolvedMedia.src))}`
        );

        if (!response.ok) return;
        const data = await response.json();

        if (cancelled) return;

        setVimeoMeta({
          thumbnail: data?.thumbnail_url || '',
          width: Number(data?.width) || 16,
          height: Number(data?.height) || 9,
        });
      } catch {
        if (!cancelled) {
          setVimeoMeta(DEFAULT_VIMEO_META);
        }
      }
    };

    loadVimeoPreview();

    return () => {
      cancelled = true;
    };
  }, [hasStarted, isOpen, resolvedMedia.isVimeo, resolvedMedia.src]);

  useEffect(() => {
    if (!resolvedMedia.isVimeo || !hasStarted || !vimeoIframeRef.current) {
      return undefined;
    }

    const player = new Player(vimeoIframeRef.current);
    vimeoPlayerRef.current = player;

    const onPlay = () => syncPlaybackState(true);
    const onPause = () => syncPlaybackState(false);
    const onEnded = () => syncPlaybackState(false);
    const onVolumeChange = (data) => {
      setIsMuted(Boolean(data?.muted));
    };

    player.on('play', onPlay);
    player.on('pause', onPause);
    player.on('ended', onEnded);
    player.on('volumechange', onVolumeChange);

    player.ready()
      .then(() => player.setMuted(vimeoStartMuted))
      .catch(() => {});

    return () => {
      player.off('play', onPlay);
      player.off('pause', onPause);
      player.off('ended', onEnded);
      player.off('volumechange', onVolumeChange);
      player.destroy().catch(() => {});

      if (vimeoPlayerRef.current === player) {
        vimeoPlayerRef.current = null;
      }
    };
  }, [hasStarted, resolvedMedia.isVimeo, syncPlaybackState, vimeoStartMuted]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const previousBodyOverflow = document.body.style.overflow;
    const previousHtmlOverflow = document.documentElement.style.overflow;

    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        closePopup();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => {
      document.body.style.overflow = previousBodyOverflow;
      document.documentElement.style.overflow = previousHtmlOverflow;
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closePopup, isOpen]);

  useEffect(() => {
    if (resolvedMedia.isVimeo || !videoRef.current) {
      return undefined;
    }

    videoRef.current.muted = isMuted;
    return undefined;
  }, [isMuted, resolvedMedia.isVimeo]);

  useEffect(() => () => {
    clearControlsHideTimeout();
  }, [clearControlsHideTimeout]);

  if (!popup || !isOpen || !resolvedMedia.src) {
    return null;
  }

  const centerControlIcon = isPlaying ? pauseIcon : playIcon;
  const centerControlLabel = isPlaying ? getUiText(locale, 'pauseVideo') : getUiText(locale, 'playVideo');
  const audioControlIcon = isMuted ? audioOffIcon : audioOnIcon;
  const audioControlLabel = isMuted ? getUiText(locale, 'unmuteAudio') : getUiText(locale, 'muteAudio');

  const renderVideoControls = () => (
    <>
      <button
        type="button"
        className={styles.popupFullscreen__interactionLayer}
        onMouseMove={handleVideoSurfaceInteraction}
        onTouchStart={handleVideoSurfaceInteraction}
        onClick={handleVideoSurfaceInteraction}
        aria-hidden="true"
        tabIndex={-1}
      />

      <button
        type="button"
        className={`${styles.popupFullscreen__playButton} ${isPlaying && !isCenterControlVisible ? styles['popupFullscreen__playButton--hidden'] : ''}`}
        onClick={handlePlaybackToggle}
        aria-label={centerControlLabel}
      >
        <img
          src={centerControlIcon}
          alt={centerControlLabel}
          title={centerControlLabel}
          className={styles.popupFullscreen__playIcon}
        />
      </button>

      <button
        type="button"
        className={styles.popupFullscreen__audioButton}
        onClick={handleMuteToggle}
        aria-label={audioControlLabel}
        aria-pressed={isMuted}
      >
        <img
          src={audioControlIcon}
          alt={audioControlLabel}
          title={audioControlLabel}
          className={styles.popupFullscreen__audioIcon}
        />
      </button>
    </>
  );

  const renderMedia = () => {
    if (resolvedMedia.mediaType === 'image') {
      return (
        <div className={styles.popupFullscreen__card}>
          <img
            src={resolvedMedia.src}
            alt={resolvedMedia.alt}
            title={label}
            className={styles.popupFullscreen__image}
          />
        </div>
      );
    }

    if (resolvedMedia.isVimeo) {
      return (
        <div
          className={`${styles.popupFullscreen__card} ${styles['popupFullscreen__card--vimeo']}`}
          style={vimeoCardStyle}
        >
          {hasStarted ? (
            <div className={styles.popupFullscreen__iframeWrap}>
              <iframe
                ref={vimeoIframeRef}
                src={getVimeoEmbedUrl(resolvedMedia.src, {
                  autoplay: true,
                  muted: vimeoStartMuted,
                })}
                title={label}
                className={styles.popupFullscreen__iframe}
                allow="autoplay; fullscreen; picture-in-picture"
                allowFullScreen
              />
            </div>
          ) : vimeoMeta.thumbnail ? (
            <img
              src={vimeoMeta.thumbnail}
              alt={resolvedMedia.alt}
              title={label}
              className={styles.popupFullscreen__vimeoPreview}
            />
          ) : (
            <div className={styles.popupFullscreen__vimeoFallback} />
          )}

          {renderVideoControls()}
        </div>
      );
    }

    return (
      <div className={styles.popupFullscreen__card}>
        <video
          ref={videoRef}
          src={resolvedMedia.src}
          poster={resolvedMedia.posterSrc || undefined}
          className={styles.popupFullscreen__nativeVideo}
          playsInline
          preload="metadata"
          controls={false}
          muted={isMuted}
          onPlay={() => syncPlaybackState(true)}
          onPause={() => syncPlaybackState(false)}
          onEnded={() => {
            if (videoRef.current) {
              videoRef.current.currentTime = 0;
            }
            syncPlaybackState(false);
          }}
          onContextMenu={(event) => event.preventDefault()}
        />

        {renderVideoControls()}
      </div>
    );
  };

  return createPortal(
    <div
      className={styles.popupFullscreen}
      data-modal="popup-fullscreen"
      role="dialog"
      aria-modal="true"
      aria-label={label}
      onClick={closePopup}
    >
      <button
        type="button"
        className={styles.popupFullscreen__close}
        onClick={(event) => {
          event.stopPropagation();
          closePopup();
        }}
        aria-label={getUiText(locale, 'closeFullscreenPopup')}
      >
        <img
          src={closeIcon}
          alt={getUiText(locale, 'closeFullscreenPopup')}
          title={getUiText(locale, 'closeFullscreenPopup')}
          aria-hidden="true"
          className={styles.popupFullscreen__closeIcon}
        />
      </button>

      <div
        className={styles.popupFullscreen__content}
        onClick={(event) => event.stopPropagation()}
      >
        {renderMedia()}
      </div>
    </div>,
    document.body
  );
};

export default PopupFullscreen;